import moment from "moment";
import styled from "styled-components";
import { merge } from "lodash-es";

import strings from "common/strings";

import { getters } from "common/components/getters/getters";
import { makeMnErrorView } from "common/components/error";
import { FetchErrorRetry } from "common/helpers/errors";
import { _parameterized } from "common/helpers/_parameterized";

import { WebixDatatable } from "common/components/webix/react/Datatable";

import { DefaultView } from "../defaultView";
import template from "./tableView.jst";

function Table({
    data,
    columns,
    onDrilldown
}) {
    const ui = {
        columns,
        rowHeight: 28,
        scroll: false,
        on: {
            onItemClick(id) {
                onDrilldown(this.getItem(id));
            },

            onAfterLoad() {
                this.config.columns.forEach(({ id, initialSorting }) => {
                    if (initialSorting) {
                        this.sort(...initialSorting);
                        this.markSorting(id, initialSorting.includes("asc") ? "asc" : "desc");
                    }
                });
            }
        }
    };

    return <WebixDatatable className="table-view" data={data} ui={ui} />;
}

const TableViewWrapper = styled.div`
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: stretch;

    .table-view {
        flex-grow: 1;
        flex-shrink: 1;
        flex-basis: 0%;
        min-height: 1000px;

        .amount,
        .value {
            text-align: right;
        }
    }
`;

export const TableView = DefaultView.extend({
    type: "table",
    template,
    regions: {
        table: ".table"
    },
    
    events: {
        "click .table-name.header": function(e) {
            e.preventDefault();
            this.drilldownToCorrespondingPage();
        },
        ...DefaultView.prototype.events
    },
    
    attributes() {
        return {
            ...DefaultView.prototype.attributes.apply(this, arguments),
            "gs-max-h": 10,
            "gs-min-w": 3,
            "gs-max-w": 12
        };
    },
    
    getLoadingChildren() {
        return this.ui.content.find("div.table");
    },
    
    getNumberOfRows() {
        const ROW_HEIGHT = 25;
        const WIDGET_HEADER_HEIGHT = 41;
        const TABLE_HEADER_HEIGHT = 27;
            
        const tableBodyHeight = this.$el.outerHeight(true) - WIDGET_HEADER_HEIGHT - TABLE_HEADER_HEIGHT;
        
        return tableBodyHeight / ROW_HEIGHT | 0;
    },

    getTableColumns() {
        const [pivot] = this.model.get("options").pivot;
        const { name, key } = getters.findPivot(pivot);
        const tableColumns = this.model.subtypeModel.getClone("table").columns;

        // Add name, apiKey, and sort attr to the first column in the table
        tableColumns[0].header = name;

        if (["sales", "refunds"].includes(this.model.get("widgetSubtype")) && pivot === "currency_alias") {
            tableColumns[0].header = strings.stl_currency;
        }
        
        const tmp = tableColumns[0].template;
        if (tmp) {
            tableColumns[0].template = (model) => tmp(model, key);
        }
        
        tableColumns[0].id = key;

        // If pivot is date_time then need to change cell format to date_time
        if (pivot === "date_time") {
        // eslint-disable-next-line sentence-case/sentence-case
            tableColumns[0].format = (d) => moment(d).format("ddd MMM D, YYYY");
        }

        return tableColumns.map((col) => merge(col, { fillspace: true }));
    },

    getCollectionSortMode() {
        const [pivot] = this.model.get("options").pivot;
        return pivot === "bank" ? "server" : "client";
    },
    
    getTableViewInstance() {
        const [pivot] = this.model.get("options").pivot;
        const apiKey = getters.findPivotKey(pivot);

        return (
            <TableViewWrapper>
                <h3 className="table-name header drag-grip">{this.makeWidgetTitle()}</h3>
                <Table
                    columns={this.getTableColumns()}
                    data={this.collection.toArray().map((model) => model.attributes)}
                    onDrilldown={(row) => this.drilldownToCorrespondingPage(row[apiKey])}
                />
            </TableViewWrapper>
        );
    },
    
    showErrorMessage() {
        this.showChildView("table", makeMnErrorView(new FetchErrorRetry({
            message: "Please refresh widget as data could not load",
            customActions: [{
                className: "retry-widget",
                label: "Refresh widget"
            }],
            hideDetails: true,
            custom: true
        }), true, false));
    },
    
    refetchWidgetData(...args) {
        this.onGridstackInit();
        return DefaultView.prototype.refetchWidgetData.apply(this, args);
    },

    handleSuccess() {
        DefaultView.prototype.handleSuccess.call(this);
        const tableView = this.getTableViewInstance();
        this.showChildView("table", tableView, { waitForAttach: true });
    },

    onGridstackInit() {
        this.loadData();
    },

    getRequestParams() {
        return {
            ...DefaultView.prototype.getRequestParams.call(this),
            ..._parameterized.getSortParamsFromTableColumns(this.getTableColumns()),
            limit: 25
        };
    }
});
